import { askGemini } from '../config/gemini';
import { BLOCKS, COMMON_FIELDS, RATING_LABELS } from './reportSchema';
import { getProgram, MEMBER_TYPES } from '../config/programs';

// ── AI kwa Programu ─────────────────────────────────────────────────────
// AI inasoma ripoti halisi zilizojazwa na staff, si makisio. Kwa sababu
// bloku ya chess ni ileile kwa programu zote, uchambuzi unafanya kazi
// hata kwa programu zitakazoongezwa baadaye.

const ALL_FIELDS = (() => {
  const map = {};
  Object.values(BLOCKS).forEach(b => b.fields.forEach(f => { map[f.key] = f; }));
  COMMON_FIELDS.forEach(f => { map[f.key] = f; });
  return map;
})();

function renderValues(values = {}) {
  const lines = [];
  Object.entries(values).forEach(([k, v]) => {
    if (v === '' || v === null || v === undefined) return;
    const f = ALL_FIELDS[k];
    if (!f) return;
    const shown = f.type === 'rating' ? `${v}/5 (${RATING_LABELS[v] || ''})` : v;
    lines.push(`  - ${f.label}: ${shown}`);
  });
  return lines.join('\n');
}

function renderReport(r) {
  const head = `[${r.weekLabel || r.weekKey}] iliyoandikwa na ${r.authorName || 'staff'}`;
  const body = renderValues(r.values);
  const extra = (r.contributions || [])
    .map(c => `  - Nyongeza kutoka ${c.byName}: ${c.text}`)
    .join('\n');
  return [head, body, extra].filter(Boolean).join('\n');
}

const STYLE = `
Jibu kwa KISWAHILI pekee, kwa lugha rahisi inayoeleweka kwa mwalimu wa kawaida.
Usitumie jargon ya kitaalamu. Usirudie data kama ilivyo — chambua maana yake.
Tumia vichwa vifupi na orodha fupi. Usizidi maneno 350.
Kama data ni ndogo mno kufikia hitimisho, sema wazi badala ya kubuni.
`.trim();

// ── Uchambuzi wa mtu mmoja ──────────────────────────────────────────────
export async function analyseMember(member, reports, programId) {
  const program = getProgram(programId);
  if (!reports.length) {
    return 'Bado hakuna ripoti ya kutosha kwa mtu huyu. Andika ripoti za wiki chache kisha rudi hapa.';
  }
  const history = reports
    .slice(0, 8)
    .reverse()
    .map(renderReport)
    .join('\n\n');

  const prompt = `
Wewe ni mshauri wa Iyanna Foundation (Arusha, Tanzania) unayesaidia makocha
wa chess na lifeskills kufuatilia maendeleo ya kila mtu.

PROGRAMU: ${program?.name || programId}
MTU: ${member.fullName} (${MEMBER_TYPES[member.type]?.label || member.type})
MAHALI: ${member.school || member.centre || member.area || 'haijaainishwa'}
NGAZI YA CHESS: ${member.chessLevel || 'haijawekwa'}

RIPOTI ZAKE (kutoka wiki za nyuma hadi karibuni):
${history}

Andika uchambuzi wenye sehemu hizi:
1. MWENENDO — anapanda, anasimama au anashuka? Onyesha ushahidi kutoka data.
2. NGUVU ZAKE — mambo 2-3 anayofanya vizuri.
3. ANAPOHANGAIKA — mambo 2-3 yanayohitaji msaada.
4. USHAURI KWA KOCHA — hatua 3 mahususi za kufanya wiki ijayo.
${member.type === 'mzazi' ? '5. BIASHARA — hali ya biashara yake na hatua ya kuboresha.' : ''}

${STYLE}
`.trim();

  return askGemini(prompt);
}

// ── Uchambuzi wa programu nzima ─────────────────────────────────────────
export async function analyseProgram({ programId, members, individual, sessions, staffWeekly }) {
  const program = getProgram(programId);
  if (!individual.length && !sessions.length) {
    return 'Bado hakuna ripoti za kutosha kwenye programu hii. AI inahitaji angalau wiki moja ya ripoti.';
  }

  const sample = individual.slice(0, 40).map(r =>
    `${r.memberName} (${r.weekKey}, na ${r.authorName}):\n${renderValues(r.values)}`
  ).join('\n\n');

  const sess = sessions.slice(0, 15).map(s =>
    `${s.date || ''} — ${s.clubName || ''} (na ${s.authorName}): ${renderValues(s.values)}\nWalihudhuria: ${s.presentCount ?? '?'} kati ya ${s.totalCount ?? '?'}`
  ).join('\n\n');

  const staff = (staffWeekly || []).slice(0, 10).map(s =>
    `${s.authorName} (${s.weekKey}):\n${renderValues(s.values)}`
  ).join('\n\n');

  const prompt = `
Wewe ni mchambuzi wa programu wa Iyanna Foundation (Arusha, Tanzania).

PROGRAMU: ${program?.name || programId}
WANACHAMA: ${members.length}
RIPOTI BINAFSI ZILIZOPO: ${individual.length}
RIPOTI ZA SESSION: ${sessions.length}

=== SAMPULI YA RIPOTI BINAFSI ===
${sample || '(hakuna)'}

=== RIPOTI ZA SESSION ===
${sess || '(hakuna)'}

=== RIPOTI ZA STAFF ===
${staff || '(hakuna)'}

Andika uchambuzi wenye sehemu hizi:
1. HALI YA JUMLA — programu inaendaje?
2. KINACHOFANYA KAZI — mambo yanayoonyesha matokeo mazuri.
3. KINACHOHITAJI KUBORESHWA — udhaifu unaojirudia kwenye ripoti.
4. MAHUDHURIO — muundo wa mahudhurio na kinachoyaathiri.
5. MAPENDEKEZO YA MAFUNZO — mabadiliko 3-4 mahususi kwenye mafunzo au activity.
6. TAHADHARI — watu au vilabu vinavyohitaji ufuatiliaji wa haraka (taja majina).

${STYLE}
`.trim();

  return askGemini(prompt);
}

// ── Msaidizi wa kuandika ripoti — anageuza maandishi mafupi kuwa ripoti ──
export async function draftFromNotes({ member, programId, notes, blockLabels }) {
  const program = getProgram(programId);
  const prompt = `
Kocha wa Iyanna Foundation ameandika maelezo mafupi baada ya session.
Yageuze kuwa muhtasari wa kitaalamu wa ripoti ya wiki.

PROGRAMU: ${program?.name || programId}
MTU: ${member.fullName} (${MEMBER_TYPES[member.type]?.label || member.type})
MAENEO YANAYORIPOTIWA: ${(blockLabels || []).join(', ')}

MAELEZO YA KOCHA:
"${notes}"

Toa vipande viwili tu, vikitenganishwa hivi:
MUHTASARI:
<aya moja au mbili, maneno 60-110, zinazoelezea wiki yake>

USHAURI:
<hatua 2-3 fupi za kufanya wiki ijayo>

Andika kwa Kiswahili. Usiongeze taarifa ambazo kocha hakuzisema.
`.trim();

  return askGemini(prompt);
}

// ── Muhtasari wa ripoti ya session ──────────────────────────────────────
export async function analyseSessions(sessions, programId) {
  const program = getProgram(programId);
  if (!sessions.length) return 'Bado hakuna ripoti za session kwenye programu hii.';
  const text = sessions.slice(0, 20).map(s =>
    `${s.date || ''} — ${s.clubName || ''} (na ${s.authorName}):\n${renderValues(s.values)}`
  ).join('\n\n');

  const prompt = `
Chambua ripoti za session za programu "${program?.name || programId}" ya Iyanna Foundation.

${text}

Andika: 1) mada zinazorudiwa, 2) changamoto zinazojirudia,
3) kinachofanya session ziwe nzuri, 4) mapendekezo 3 ya kuboresha session zijazo.

${STYLE}
`.trim();

  return askGemini(prompt);
}

// ── Uchambuzi wa PROGRAMU ZOTE kwa pamoja ───────────────────────────────
// Swali linalojibiwa hapa: "mbinu ya chess + lifeskills imetusaidiaje
// kwenye kila programu?" — inawezekana kwa sababu bloku hizo mbili ni
// zilezile kila mahali.
export async function analyseAcrossPrograms(rows) {
  const usable = rows.filter(r => r.summary.count > 0);
  if (!usable.length) {
    return 'Bado hakuna ripoti za kutosha kulinganisha programu. Andika ripoti za wiki chache kisha rudi hapa.';
  }

  const body = usable.map(r => {
    const s = r.summary;
    const chess = Object.entries(s.chess)
      .filter(([, v]) => v)
      .map(([k, v]) => `${k}=${v}/5`).join(', ');
    const life = Object.entries(s.life)
      .filter(([, v]) => v)
      .map(([k, v]) => `${k}=${v}/5`).join(', ');
    const trend = (r.trend || [])
      .map(t => `${t.weekKey}: chess ${t.chess ?? '-'}, lifeskills ${t.life ?? '-'} (ripoti ${t.count})`)
      .join('\n    ');
    return `
PROGRAMU: ${r.name}
  Wanachama: ${r.members} | Ripoti binafsi: ${s.count}
  Wastani wa chess: ${s.chessAvg ?? '-'} /5 (${chess || 'hakuna'})
  Wastani wa lifeskills: ${s.lifeAvg ?? '-'} /5 (${life || 'hakuna'})
  Mahudhurio kamili: chess ${s.chessAttendance ?? '-'}%, lifeskills ${s.lifeAttendance ?? '-'}%
  Michezo: ${s.gamesPlayed} ilichezwa, ushindi ${s.winRate ?? '-'}%
  Ngazi: ${Object.entries(s.levels).map(([k, v]) => `${k} ${v}`).join(', ') || 'hazijawekwa'}
  Mwenendo wa wiki:
    ${trend || '(wiki moja tu)'}`;
  }).join('\n');

  const prompt = `
Wewe ni mchambuzi wa Iyanna Foundation (Arusha, Tanzania). Foundation
inafundisha CHESS na LIFESKILLS kwenye programu zote — vipimo ni vilevile
kila mahali, hivyo programu zinalinganishwa moja kwa moja.

${body}

Jibu maswali haya:
1. LINGANISHA — programu ipi inafanya vizuri zaidi kwenye chess? Kwenye lifeskills? Kwa nini pengine?
2. MBINU YA CHESS + LIFESKILLS — kuna ushahidi gani kwamba inasaidia?
   Angalia kama alama za lifeskills (tabia, ushirikiano, umakini) zinapanda
   pamoja na alama za chess. Sema wazi kama ushahidi bado ni mdogo.
3. TOFAUTI — programu ipi ina pengo kubwa kati ya chess na lifeskills, na maana yake?
4. KUJIFUNZA KUTOKA KWA WENZAO — kinachofanyika kwenye programu bora
   ambacho nyingine zinaweza kunakili.
5. HATUA 3 — mabadiliko mahususi ya kufanya mwezi ujao.

${STYLE}
`.trim();

  return askGemini(prompt);
}
